import HolidayModal from '@/components/HolidayModal'
import RequestLeaveDialog from '@/components/RequestLeaveDialog'
import { CustomPagination } from '@/components/CustomPagination'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDebounce } from '@/lib/useDebounce'
import {
  useDeleteHolidayMutation,
  useGetHolidaysQuery,
} from '@/redux/service/holiday'
import type { Holiday } from '@/redux/service/holiday'
import {
  useGetLeaveReportQuery,
  useEmailLeaveReportMutation,
} from '@/redux/service/leaveRequest'
import { useGetLeaveBalancesQuery } from '@/redux/service/leaveBalance'
import ReusableDateRangePicker, {
  type DateRange,
} from '@/components/ReusableDateRangePicker'
import { format } from 'date-fns'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Edit,
  Trash,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  CalendarDays,
  Plus,
  Search,
  Users,
  Filter,
  // CalendarPlus,
  Mail,
} from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { ColumnVisibilityPopover } from '@/components/ColumnVisibilityPopover'
import { cn, dateFormatDMY, dateTimeFormatDMY } from '@/lib/utils'

const DEFAULT_HOLIDAY_COLUMNS = [
  { key: 'name', label: 'Holiday Name' },
  { key: 'date', label: 'Date' },
  { key: 'description', label: 'Description' },
  { key: 'status', label: 'Status' },
]

const DEFAULT_LEAVE_COLUMNS = [
  { key: 'employee', label: 'Employee' },
  { key: 'department', label: 'Department' },
  { key: 'leave_type', label: 'Leave Type' },
  { key: 'duration', label: 'Duration' },
  { key: 'from', label: 'From' },
  { key: 'to', label: 'To' },
  { key: 'days', label: 'Days' },
  { key: 'reason', label: 'Reason' },
  { key: 'status', label: 'Status' },
  { key: 'balance', label: 'Remaining Balance' },
  { key: 'requested', label: 'Requested' },
]

const Holidays = () => {
  // ─── Tab ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<1 | 2>(1)

  // ─── Holidays state ────────────────────────────────────────────────────
  const [page, setPage] = useState<number>(1)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [limit, setLimit] = useState<number>(20)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [selectedHolidayId, setSelectedHolidayId] = useState<number | null>(
    null
  )
  const debouncedSearch = useDebounce(searchQuery, 500)
  const [editData, setEditData] = useState<Holiday | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isRequestLeaveOpen, setIsRequestLeaveOpen] = useState(false)
  const [sortBy, setSortBy] = useState<string | undefined>()
  const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()
  const [selectedYear, setSelectedYear] = useState<number | undefined>()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Holidays column state
  const [holidayColumns, setHolidayColumns] = useState<
    { key: string; label: string }[]
  >(() => {
    const saved = localStorage.getItem('holiday-table-column-order')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return parsed
          .map((key: string) =>
            DEFAULT_HOLIDAY_COLUMNS.find(c => c.key === key)
          )
          .filter(Boolean) as { key: string; label: string }[]
      } catch {
        /* fallback */
      }
    }
    return DEFAULT_HOLIDAY_COLUMNS
  })

  const [selectedHolidayColumns, setSelectedHolidayColumns] = useState<
    string[]
  >(() => {
    const saved = localStorage.getItem('holiday-table-columns')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        /* fallback */
      }
    }
    return DEFAULT_HOLIDAY_COLUMNS.map(col => col.key)
  })

  // Leaves column state
  const [leaveColumns, setLeaveColumns] = useState<
    { key: string; label: string }[]
  >(() => {
    const saved = localStorage.getItem('leave-table-column-order')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return parsed
          .map((key: string) => DEFAULT_LEAVE_COLUMNS.find(c => c.key === key))
          .filter(Boolean) as { key: string; label: string }[]
      } catch {
        /* fallback */
      }
    }
    return DEFAULT_LEAVE_COLUMNS
  })

  const [selectedLeaveColumns, setSelectedLeaveColumns] = useState<string[]>(
    () => {
      const saved = localStorage.getItem('leave-table-columns')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch {
          /* fallback */
        }
      }
      return DEFAULT_LEAVE_COLUMNS.map(col => col.key)
    }
  )

  useEffect(() => {
    localStorage.setItem(
      'holiday-table-column-order',
      JSON.stringify(holidayColumns.map(c => c.key))
    )
    localStorage.setItem(
      'holiday-table-columns',
      JSON.stringify(selectedHolidayColumns)
    )
  }, [holidayColumns, selectedHolidayColumns])

  useEffect(() => {
    localStorage.setItem(
      'leave-table-column-order',
      JSON.stringify(leaveColumns.map(c => c.key))
    )
    localStorage.setItem(
      'leave-table-columns',
      JSON.stringify(selectedLeaveColumns)
    )
  }, [leaveColumns, selectedLeaveColumns])

  const handleHolidayDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setHolidayColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleLeaveDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setLeaveColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleHolidayColumn = (key: string) => {
    setSelectedHolidayColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedHolidayColumns.length > 1)
        : [...prev, key]
    )
  }

  const toggleLeaveColumn = (key: string) => {
    setSelectedLeaveColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedLeaveColumns.length > 1)
        : [...prev, key]
    )
  }

  const isHolidayColumnVisible = (key: string) =>
    selectedHolidayColumns.includes(key)
  const isLeaveColumnVisible = (key: string) =>
    selectedLeaveColumns.includes(key)

  const visibleHolidayColumnsCount = useMemo(() => {
    return (
      holidayColumns.filter(c => selectedHolidayColumns.includes(c.key))
        .length + 1
    )
  }, [holidayColumns, selectedHolidayColumns])

  const visibleLeaveColumnsCount = useMemo(() => {
    return leaveColumns.filter(c => selectedLeaveColumns.includes(c.key)).length
  }, [leaveColumns, selectedLeaveColumns])

  // ─── Leaves state ─────────────────────────────────────────────────────
  const [leavesPage, setLeavesPage] = useState<number>(1)
  const [leavesLimit, setLeavesLimit] = useState<number>(100)
  const [leavesSearch, setLeavesSearch] = useState<string>('')
  const [leavesStatus, setLeavesStatus] = useState<string>('approved')
  const [leavesDateRange, setLeavesDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  })
  const [leavesSortBy, setLeavesSortBy] = useState<string | undefined>()
  const [leavesOrder, setLeavesOrder] = useState<'ASC' | 'DESC' | undefined>()
  const debouncedLeavesSearch = useDebounce(leavesSearch, 500)

  // ─── Holidays helpers ──────────────────────────────────────────────────
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') {
        setOrder('DESC')
      } else if (order === 'DESC') {
        setSortBy(undefined)
        setOrder(undefined)
      }
    } else {
      setSortBy(column)
      setOrder('ASC')
    }
    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-2 opacity-50 inline-block' />
    if (order === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-2 inline-block' />
    return <ArrowDown className='w-3 h-3 ml-2 inline-block' />
  }

  // ─── Leaves helpers ────────────────────────────────────────────────────
  const handleLeavesSort = (column: string) => {
    if (leavesSortBy === column) {
      if (leavesOrder === 'ASC') {
        setLeavesOrder('DESC')
      } else if (leavesOrder === 'DESC') {
        setLeavesSortBy(undefined)
        setLeavesOrder(undefined)
      }
    } else {
      setLeavesSortBy(column)
      setLeavesOrder('ASC')
    }
    setLeavesPage(1)
  }

  const getLeaveSortIcon = (column: string) => {
    if (leavesSortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-2 opacity-50 inline-block' />
    if (leavesOrder === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-2 inline-block' />
    return <ArrowDown className='w-3 h-3 ml-2 inline-block' />
  }

  // ─── Queries ───────────────────────────────────────────────────────────
  const {
    data: currentData,
    isLoading,
    isFetching,
  } = useGetHolidaysQuery({
    page,
    limit,
    search: debouncedSearch,
    year: selectedYear,
    sort_by: sortBy,
    order,
  })

  const {
    data: leavesData,
    isLoading: isLeavesLoading,
    isFetching: isLeavesFetching,
  } = useGetLeaveReportQuery({
    page: leavesPage,
    limit: leavesLimit,
    search: debouncedLeavesSearch,
    ...(leavesStatus !== 'all' ? { status: leavesStatus } : {}),
    ...(leavesDateRange?.from
      ? { startDate: format(leavesDateRange.from, 'yyyy-MM-dd') }
      : {}),
    ...(leavesDateRange?.to
      ? { endDate: format(leavesDateRange.to, 'yyyy-MM-dd') }
      : {}),
    ...(leavesSortBy ? { sort_by: leavesSortBy } : {}),
    ...(leavesOrder ? { order: leavesOrder } : {}),
  })

  // Fetched once (not per-row) and looked up by employee+type — gives each
  // leave row its "remaining balance" without an N+1 request per row. Only
  // needed while the Leaves tab + balance column are visible.
  const { data: balancesData } = useGetLeaveBalancesQuery(
    { limit: 100 },
    { skip: activeTab !== 2 || !selectedLeaveColumns.includes('balance') }
  )
  const balanceByEmployeeType = useMemo(() => {
    const map = new Map<string, number>()
    for (const b of balancesData?.data ?? []) {
      map.set(`${b.employee_id}::${b.leave_type}`, b.balance)
    }
    return map
  }, [balancesData])

  const totalPages = currentData?.pagination?.totalPages ?? 1
  const totalRecords = currentData?.pagination?.total ?? 0
  const leavesTotalPages = leavesData?.pagination?.totalPages ?? 1
  const leavesTotalRecords = leavesData?.pagination?.total ?? 0
  const [deleteHoliday, { isLoading: isDeleting }] = useDeleteHolidayMutation()
  const [emailLeaveReport, { isLoading: isEmailingReport }] =
    useEmailLeaveReportMutation()

  const handleEmailLeaveReport = async () => {
    try {
      const res = await emailLeaveReport({}).unwrap()
      toast.success(res.message || 'Leave report emailed')
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to email leave report'
      toast.error(message)
    }
  }

  // ─── Holiday handlers ──────────────────────────────────────────────────
  const handleEdit = (holiday: Holiday) => {
    setEditData(holiday)
    setIsModalOpen(true)
  }

  const handleDeleteClick = (id: number) => {
    setSelectedHolidayId(id)
    setIsDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedHolidayId) return
    try {
      await deleteHoliday({ id: selectedHolidayId }).unwrap()
      toast.success('Holiday deleted successfully')
      setIsDeleteOpen(false)
      setSelectedHolidayId(null)
    } catch {
      toast.error('Failed to delete holiday')
    }
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setSortBy(undefined)
    setOrder(undefined)
    setSelectedYear(undefined)
    setPage(1)
  }

  const handleClearLeavesFilters = () => {
    setLeavesSearch('')
    setLeavesStatus('approved')
    setLeavesDateRange({ from: undefined, to: undefined })
    setLeavesSortBy(undefined)
    setLeavesOrder(undefined)
    setLeavesPage(1)
  }

  // ─── Date formatters ───────────────────────────────────────────────────
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    })
  }

  const getHolidayStatus = (dateStr: string) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const holidayDate = new Date(dateStr)
    holidayDate.setHours(0, 0, 0, 0)

    if (holidayDate.getTime() === today.getTime()) {
      return 'HOLIDAY'
    }

    return holidayDate > today ? 'UPCOMING' : 'PAST'
  }

  // ─── Table content: Holidays ───────────────────────────────────────────
  let holidayTableContent
  if (isLoading || isFetching) {
    holidayTableContent = (
      <TableRow>
        <TableCell colSpan={visibleHolidayColumnsCount} className='h-40' />
      </TableRow>
    )
  } else if ((currentData?.data?.length || 0) === 0) {
    holidayTableContent = (
      <TableRow>
        <TableCell
          colSpan={visibleHolidayColumnsCount}
          className='h-40 text-center'
        >
          <div className='flex flex-col items-center justify-center space-y-3'>
            <CalendarDays className='w-10 h-10 text-muted-foreground/40' />
            <p className='text-muted-foreground text-sm font-medium'>
              No holidays found
            </p>
            <p className='text-muted-foreground/60 text-xs'>
              Click "Add Holiday" to create one
            </p>
          </div>
        </TableCell>
      </TableRow>
    )
  } else {
    holidayTableContent = currentData?.data?.map(holiday => (
      <TableRow
        key={holiday.id}
        className='group hover:bg-muted/50 transition-colors'
      >
        {holidayColumns.map(column => {
          if (!isHolidayColumnVisible(column.key)) return null
          switch (column.key) {
            case 'name':
              return (
                <TableCell key={column.key} className='font-medium'>
                  <div className='flex items-center gap-3'>
                    <div
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        getHolidayStatus(holiday.date) === 'HOLIDAY'
                          ? 'bg-blue-500'
                          : getHolidayStatus(holiday.date) === 'UPCOMING'
                            ? 'bg-emerald-500'
                            : 'bg-slate-300'
                      }`}
                    />
                    {holiday.name}
                  </div>
                </TableCell>
              )
            case 'date':
              return (
                <TableCell key={column.key}>
                  <span className='inline-flex items-center gap-2 bg-muted px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground'>
                    <CalendarDays className='w-3.5 h-3.5 text-muted-foreground/70' />
                    {formatDate(holiday.date)}
                  </span>
                </TableCell>
              )
            case 'description':
              return (
                <TableCell
                  key={column.key}
                  className='text-muted-foreground max-w-[300px] truncate'
                >
                  {holiday.description || (
                    <span className='text-slate-300 italic'>
                      No description
                    </span>
                  )}
                </TableCell>
              )
            case 'status':
              return (
                <TableCell key={column.key}>
                  {getHolidayStatus(holiday.date) === 'HOLIDAY' ? (
                    <span className='inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200'>
                      Holiday
                    </span>
                  ) : getHolidayStatus(holiday.date) === 'UPCOMING' ? (
                    <span className='inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200'>
                      Upcoming
                    </span>
                  ) : (
                    <span className='inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-50 text-slate-500 border border-slate-200'>
                      Past
                    </span>
                  )}
                </TableCell>
              )
            default:
              return null
          }
        })}
        <TableCell className='sticky right-0 z-10 bg-background flex items-center gap-3 border-l border-border shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)] opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
          <Button
            variant='outline'
            size='sm'
            className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground'
            onClick={() => handleEdit(holiday)}
          >
            <Edit className='w-4 h-4' />
          </Button>
          <Button
            variant='outline'
            size='sm'
            className='w-11 bg-background border-border hover:bg-accent hover:text-destructive'
            onClick={() => handleDeleteClick(holiday.id)}
            disabled={isDeleting}
          >
            <Trash className='w-4 h-4' />
          </Button>
        </TableCell>
      </TableRow>
    ))
  }

  // ─── Table content: Leaves ─────────────────────────────────────────────
  let leavesTableContent
  if (isLeavesLoading || isLeavesFetching) {
    leavesTableContent = (
      <TableRow>
        <TableCell colSpan={visibleLeaveColumnsCount} className='h-40' />
      </TableRow>
    )
  } else if ((leavesData?.data?.length || 0) === 0) {
    leavesTableContent = (
      <TableRow>
        <TableCell
          colSpan={visibleLeaveColumnsCount}
          className='h-40 text-center'
        >
          <div className='flex flex-col items-center justify-center space-y-3'>
            <Users className='w-10 h-10 text-muted-foreground/40' />
            <p className='text-muted-foreground text-sm font-medium'>
              No leave records found
            </p>
          </div>
        </TableCell>
      </TableRow>
    )
  } else {
    leavesTableContent = leavesData?.data?.map(leave => (
      <TableRow
        key={`${leave.employee_id}-${leave.from_date}-${leave.to_date}`}
        className='group '
      >
        {leaveColumns.map(column => {
          if (!isLeaveColumnVisible(column.key)) return null
          switch (column.key) {
            case 'employee':
              return (
                <TableCell
                  key={column.key}
                  className='font-medium truncate flex items-center gap-3'
                >
                  <div className='w-8 h-8 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center border border-border shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200'>
                    {leave?.profile_picture ? (
                      <img
                        src={leave?.profile_picture}
                        alt={leave?.user_name || 'User Profile'}
                        className='w-full h-full object-cover'
                        onError={e => {
                          ;(e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    ) : (
                      <span className='text-xs font-bold  text-primary'>
                        {leave?.user_name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className='font-bold text-foreground'>
                    {leave.user_name}
                  </span>
                </TableCell>
              )
            case 'department':
              return (
                <TableCell key={column.key} className='truncate'>
                  {leave.department || leave.team || '-'}
                </TableCell>
              )
            case 'leave_type':
              return (
                <TableCell key={column.key} className='truncate'>
                  {leave.leave_type}
                </TableCell>
              )
            case 'duration':
              return (
                <TableCell key={column.key} className='truncate'>
                  {leave.leave_duration}
                </TableCell>
              )
            case 'from':
              return (
                <TableCell key={column.key} className='truncate '>
                  <span className='flex gap-1 items-center'>
                    <CalendarDays className='w-3.5 h-3.5 text-muted-foreground/70' />
                    {dateFormatDMY(leave.from_date)}
                  </span>
                </TableCell>
              )
            case 'to':
              return (
                <TableCell key={column.key} className='truncate '>
                  <span className='flex gap-1 items-center'>
                    <CalendarDays className='w-3.5 h-3.5 text-muted-foreground/70' />
                    {dateFormatDMY(leave.to_date)}
                  </span>
                </TableCell>
              )
            case 'days':
              return (
                <TableCell key={column.key} className='text-center truncate'>
                  {Number(leave.total_leave_days).toFixed(1)}
                </TableCell>
              )
            case 'reason':
              return (
                <TableCell key={column.key} className='max-w-[200px] truncate'>
                  {leave.reason ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='block truncate text-muted-foreground cursor-default'>
                          {leave.reason}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className='max-w-xs whitespace-normal'>
                        {leave.reason}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className='text-slate-300 italic'>-</span>
                  )}
                </TableCell>
              )
            case 'status':
              return (
                <TableCell key={column.key}>
                  {leave.status.toLowerCase() === 'approved' ? (
                    <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-600 border border-green-500/20'>
                      Approved
                    </span>
                  ) : leave.status.toLowerCase() === 'rejected' ? (
                    <span className='iinline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive border border-destructive/20'>
                      Rejected
                    </span>
                  ) : leave.status.toLowerCase() === 'cancelled' ? (
                    <span className='iinline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive border border-destructive/20'>
                      cancelled
                    </span>
                  ) : (
                    <span className='inline-flex truncate items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200'>
                      Pending Approval
                    </span>
                  )}
                </TableCell>
              )
            case 'balance': {
              const remaining = balanceByEmployeeType.get(
                `${leave.employee_id}::${leave.leave_type}`
              )
              return (
                <TableCell key={column.key} className='text-center truncate'>
                  {remaining !== undefined ? (
                    <span
                      className={cn(
                        'font-semibold',
                        remaining <= 0 && 'text-destructive'
                      )}
                    >
                      {remaining}
                    </span>
                  ) : (
                    <span className='text-slate-300 italic'>-</span>
                  )}
                </TableCell>
              )
            }
            case 'requested':
              return (
                <TableCell
                  key={column.key}
                  className='truncate text-muted-foreground text-xs'
                >
                  {dateTimeFormatDMY(leave.created_at)}
                </TableCell>
              )
            default:
              return null
          }
        })}
      </TableRow>
    ))
  }

  return (
    <TooltipProvider>
      <div className='h-full flex flex-col space-y-4 p-2 overflow-hidden'>
        <div className='shrink-0 flex flex-col lg:flex-row lg:items-center justify-between border-b border-border pb-2 gap-4'>
          <div className='flex p-1 bg-muted border border-border rounded-xl gap-1 shrink-0 w-fit h-9 items-stretch'>
            <button
              onClick={() => setActiveTab(1)}
              className={`px-4 text-xs font-medium transition-all duration-200 rounded-lg flex items-center justify-center ${
                activeTab === 1
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
              }`}
            >
              Holidays
            </button>
            <button
              onClick={() => setActiveTab(2)}
              className={`px-4 text-xs font-medium transition-all duration-200 rounded-lg flex items-center justify-center ${
                activeTab === 2
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
              }`}
            >
              Employee Leaves
            </button>
          </div>

          <div className='flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto'>
            {activeTab === 1 ? (
              <div className=' w-full sm:w-auto flex items-center gap-3'>
                <div className='flex items-center'>
                  <Button
                    variant={'secondary'}
                    className=' text-foreground bg-background hover:bg-black hover:text-white gap-2 h-9'
                    onClick={() => setIsModalOpen(true)}
                  >
                    <Plus className='w-4 h-4' />
                    Add Holiday
                  </Button>

                  <div className='relative ml-3 hidden sm:block'>
                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
                    <Input
                      placeholder='Search holidays...'
                      value={searchQuery}
                      onChange={e => {
                        setSearchQuery(e.target.value)
                        setPage(1)
                      }}
                      className='pl-10 h-9 border-border text-foreground focus:border-primary transition-all rounded-lg text-xs bg-card'
                    />
                  </div>
                </div>
                <Select
                  value={selectedYear?.toString() ?? 'all'}
                  onValueChange={value => {
                    setSelectedYear(value === 'all' ? undefined : Number(value))
                    setPage(1)
                  }}
                >
                  <SelectTrigger className='w-full sm:w-[140px] h-9 border-border text-foreground rounded-lg text-xs bg-card'>
                    <SelectValue placeholder='Filter by year' />
                  </SelectTrigger>
                  <SelectContent className='bg-popover border-border text-foreground text-xs'>
                    <SelectItem value='all'>All Years</SelectItem>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(searchQuery || sortBy || selectedYear) && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleClearFilters}
                    className='h-9 px-3 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5'
                  >
                    <X className='w-3.5 h-3.5' /> Clear All
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* <Button
                  variant='secondary'
                  className='text-foreground bg-background hover:bg-black hover:text-white gap-2 h-9 shrink-0'
                  onClick={() => setIsRequestLeaveOpen(true)}
                >
                  <CalendarPlus className='w-4 h-4' />
                  Request Leave
                </Button> */}
                <div className='relative w-full sm:w-60 shrink-0'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
                  <Input
                    placeholder='Search employees...'
                    value={leavesSearch}
                    onChange={e => {
                      setLeavesSearch(e.target.value)
                      setLeavesPage(1)
                    }}
                    className='pl-10 h-9 border-border text-foreground focus:border-primary transition-all rounded-lg text-xs bg-card'
                  />
                </div>
                <Select
                  value={leavesStatus}
                  onValueChange={value => {
                    setLeavesStatus(value)
                    setLeavesPage(1)
                  }}
                >
                  <SelectTrigger className='w-full sm:w-[140px] h-9 border-border text-foreground rounded-lg text-xs bg-card'>
                    <div className='flex items-center gap-1.5'>
                      <Filter className='w-3.5 h-3.5 text-slate-500 shrink-0' />
                      <SelectValue placeholder='All Status' />
                    </div>
                  </SelectTrigger>
                  <SelectContent className='bg-popover border-border text-foreground text-xs'>
                    <SelectItem value='all'>All Status</SelectItem>
                    <SelectItem value='approved'>Approved</SelectItem>
                    <SelectItem value='pending approval'>
                      Pending Approval
                    </SelectItem>
                    <SelectItem value='rejected'>Rejected</SelectItem>
                    <SelectItem value='cancelled'>Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <ReusableDateRangePicker
                  onSelect={val => {
                    setLeavesDateRange(val)
                    setLeavesPage(1)
                  }}
                  className=' border-border rounded-lg bg-card text-foreground text-xs w-full sm:w-auto'
                />
                {(leavesSearch !== '' ||
                  leavesStatus !== 'approved' ||
                  leavesDateRange?.from ||
                  leavesDateRange?.to ||
                  leavesSortBy) && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleClearLeavesFilters}
                    className='h-9 px-3 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5'
                  >
                    <X className='w-3.5 h-3.5' /> Clear All
                  </Button>
                )}
                <Button
                  variant='outline'
                  size='sm'
                  className='h-9 gap-2 text-xs border-border bg-card text-foreground hover:bg-accent/30 transition-all rounded-lg px-3 shrink-0'
                  onClick={handleEmailLeaveReport}
                  disabled={isEmailingReport}
                >
                  <Mail className='w-3.5 h-3.5' />
                  {isEmailingReport ? 'Sending...' : 'Email My Report'}
                </Button>
              </>
            )}

            <ColumnVisibilityPopover
              columns={activeTab === 1 ? holidayColumns : leaveColumns}
              isColumnVisible={
                activeTab === 1 ? isHolidayColumnVisible : isLeaveColumnVisible
              }
              onToggle={
                activeTab === 1 ? toggleHolidayColumn : toggleLeaveColumn
              }
              onReset={() => {
                if (activeTab === 1) {
                  setHolidayColumns(DEFAULT_HOLIDAY_COLUMNS)
                  setSelectedHolidayColumns(
                    DEFAULT_HOLIDAY_COLUMNS.map(c => c.key)
                  )
                } else {
                  setLeaveColumns(DEFAULT_LEAVE_COLUMNS)
                  setSelectedLeaveColumns(DEFAULT_LEAVE_COLUMNS.map(c => c.key))
                }
              }}
              onDragEnd={
                activeTab === 1 ? handleHolidayDragEnd : handleLeaveDragEnd
              }
              sensors={sensors}
            />
          </div>
        </div>

        {/* ── Holidays Tab ─────────────────────────────────────────────────── */}

        {activeTab === 1 && (
          <div className='flex-1 flex flex-col overflow-hidden min-h-0'>
            {/* Table */}
            <div className='flex-1 relative overflow-hidden rounded-sm border border-border bg-card shadow-sm'>
              <Table className='border-none'>
                <TableHeader className='bg-muted/50'>
                  <TableRow className='hover:bg-muted/50 border-border'>
                    {holidayColumns.map(column => {
                      if (!isHolidayColumnVisible(column.key)) return null
                      const sortKey = ['name', 'date'].includes(column.key)
                        ? column.key
                        : undefined
                      return (
                        <TableHead
                          key={column.key}
                          className={cn(
                            'font-medium py-4',
                            sortKey && 'cursor-pointer select-none'
                          )}
                          onClick={
                            sortKey ? () => handleSort(sortKey) : undefined
                          }
                        >
                          <div className='flex items-center'>
                            {column.label} {sortKey && getSortIcon(sortKey)}
                          </div>
                        </TableHead>
                      )
                    })}
                    <TableHead className=' font-medium w-[100px] py-4'>
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{holidayTableContent}</TableBody>
              </Table>
              {(isLoading || isFetching) && (
                <TableLoadingOverlay label='Loading Holidays...' />
              )}
            </div>

            {(currentData?.data?.length ?? 0) > 0 &&
              !isLoading &&
              !isFetching && (
                <div className='shrink-0 pt-4'>
                  <CustomPagination
                    limit={limit}
                    setLimit={setLimit}
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    title='Holidays'
                  />
                </div>
              )}
          </div>
        )}

        {/* ── Leaves Tab ───────────────────────────────────────────────────── */}
        {activeTab === 2 && (
          <div className='flex-1 flex flex-col overflow-hidden min-h-0'>
            {/* Table */}
            <div className='flex-1 relative overflow-hidden rounded-sm border border-border bg-card shadow-sm'>
              <Table className='border-none'>
                <TableHeader className='bg-muted/50'>
                  <TableRow className='hover:bg-muted/50 border-border'>
                    {leaveColumns.map(column => {
                      if (!isLeaveColumnVisible(column.key)) return null
                      const sortKeyMap: Record<string, string> = {
                        employee: 'user_name',
                        from: 'from_date',
                        to: 'to_date',
                        days: 'total_leave_days',
                        status: 'status',
                      }
                      const sortKey = sortKeyMap[column.key]
                      return (
                        <TableHead
                          key={column.key}
                          className={cn(
                            'font-medium py-4',
                            sortKey && 'cursor-pointer select-none'
                          )}
                          onClick={
                            sortKey
                              ? () => handleLeavesSort(sortKey)
                              : undefined
                          }
                        >
                          <div
                            className={cn(
                              'flex items-center',
                              column.key === 'days' && 'justify-center'
                            )}
                          >
                            {column.label}{' '}
                            {sortKey && getLeaveSortIcon(sortKey)}
                          </div>
                        </TableHead>
                      )
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>{leavesTableContent}</TableBody>
              </Table>
              {(isLeavesLoading || isLeavesFetching) && (
                <TableLoadingOverlay label='Loading Leaves...' />
              )}
            </div>

            {(leavesData?.data?.length ?? 0) > 0 &&
              !isLeavesLoading &&
              !isLeavesFetching && (
                <div className='shrink-0 pt-4'>
                  <CustomPagination
                    limit={leavesLimit}
                    setLimit={setLeavesLimit}
                    page={leavesPage}
                    setPage={setLeavesPage}
                    totalPages={leavesTotalPages}
                    totalRecords={leavesTotalRecords}
                    title='Employee Leaves'
                  />
                </div>
              )}
          </div>
        )}

        {/* Create/Edit Modal */}
        <HolidayModal
          data={editData}
          isOpen={isModalOpen}
          isClose={() => {
            setIsModalOpen(false)
            setEditData(null)
          }}
        />

        <RequestLeaveDialog
          isOpen={isRequestLeaveOpen}
          isClose={() => setIsRequestLeaveOpen(false)}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <DialogContent className='max-w-md p-6 bg-background border-border text-foreground rounded-lg shadow-lg'>
            <DialogHeader>
              <DialogTitle className='text-lg font-semibold text-foreground'>
                Delete Holiday
              </DialogTitle>
            </DialogHeader>

            <div className='mt-2 text-sm text-slate-400'>
              Are you sure you want to delete this holiday? This action cannot
              be undone.
            </div>

            <DialogFooter className='mt-4 flex justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                className='bg-transparent border-[#262626] text-slate-400 hover:bg-[#262626] hover:text-white'
                onClick={() => setIsDeleteOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>

              <Button
                type='button'
                variant='destructive'
                className='bg-rose-600 hover:bg-rose-700 text-white'
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  )
}

export default Holidays
